import { useEffect } from 'react';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import { getRawStructureRequestArgs } from '../../selectors/sdmx';
import { getRequestArgs } from '@sis-cc/dotstatsuite-sdmxjs';
import { rules2 } from '@sis-cc/dotstatsuite-components';
import useSdmxQueries from './useSdmxQueries';

export default ({ hclRefs = [], afterHook = R.identity }) => {
  const { datasource } = useSelector(getRawStructureRequestArgs);
  const isEnabled = !R.isEmpty(hclRefs);

  const requestsArgs = R.map(({ agencyId, code, version }) => {
    return getRequestArgs({
      identifiers: { agencyId, code, version },
      datasource,
      type: 'hierarchicalcodelist',
    });
  }, hclRefs);

  const ctx = {
    method: 'getHierarchicalCodelist',
    requestsArgs,
  };

  const transformerHook = hcl => {
    const { agencyID: agencyId, id: code, version } = R.head(
      R.pathOr([], ['data', 'hierarchicalCodelists'], hcl),
    );
    const correspondingRefs = R.filter(
      ref =>
        ref.agencyId === agencyId &&
        ref.code === code &&
        ref.version === version,
      hclRefs,
    );
    return R.reduce(
      (memo, { hierarchy, codelistId }) => {
        return {
          ...memo,
          [codelistId]: {
            parsed: rules2.parseHierarchicalCodelist(hcl, hierarchy),
            hCodes: R.pipe(
              R.pathOr([], ['data', 'hierarchicalCodelists', 0, 'hierarchies']),
              R.find(R.propEq(hierarchy, 'id')),
              (h) => (R.isNil(h) ? [] : h.hierarchicalCodes || []),
            )(hcl),
          },
        };
      },
      {},
      correspondingRefs,
    );
  };

  const { queries } = useSdmxQueries(ctx, {
    isEnabled,
    transformerHook,
    isNotCached: true,
  });

  const isLoading = R.any(R.prop('isLoading'))(queries);

  useEffect(() => {
    if (!isLoading && isEnabled) {
      const res = R.reduce(
        (acc, { data = {} }) => {
          const ids = R.keys(data);
          const gne = R.reduce(
            (_memo, codelistId) =>
              R.pipe(
                R.assocPath(
                  ['parsed', codelistId],
                  R.path([codelistId, 'parsed'], data),
                ),
                R.assocPath(
                  ['hCodes', codelistId],
                  R.path([codelistId, 'hCodes'], data),
                ),
              )(_memo),
            acc,
            ids,
          );
          return gne;
        },
        {},
        queries,
      );
      afterHook(res);
    }
  }, [isLoading]);

  return queries;
};
